import { ref, computed, watch, onMounted } from 'vue';
import { useDisplay } from 'vuetify';
import { useComponentI18n } from '@rdrive/i18n';
import { useFilterContextRefresh } from '@rdrive/data-filters';
import { useDebounce } from '@rdrive/vue-debounce';
import messages from './messages/index.js';

// The mobile table's one header; its header row is hidden by style.css
const MOBILE_HEADERS = [
    { title: 'Items', key: 'items', sortable: false }
];

// The default desktop row's cell value: `column.key` may be a dotted path
const getNestedValue = (obj, path) => {
    return path.split('.').reduce((o, p) => o?.[p], obj);
};

export default (props, { emit, expose }) => {
    // LOCAL composer with the warn flags off (see @rdrive/i18n): `search`/`noData`/`itemsPerPage`
    // resolve from ./messages, a header's `titleKey` through the root composer. Its `locale`
    // follows the app's (inheritLocale) and is the mobile table's `:locale`.
    const i18n = useComponentI18n(messages);
    const { t } = i18n;
    const { mobile, mdAndUp } = useDisplay();
    const isMobile = computed(() => mobile.value && !mdAndUp.value);

    // State. `page`, `itemsPerPage` and `sortBy` are v-model'd on both server tables: the TABLE
    // owns them — a pagination click, a page-size change or a header click moves them, and the
    // table then emits ONE `update:options` → loadItems. Nothing here writes them back after a
    // load, so the footer always describes the page that was asked for.
    const loading = ref(false);
    const items = ref([]);
    const totalItems = ref(0);
    const page = ref(1);
    const itemsPerPage = ref(10);
    const sortBy = ref(props.defaultSort.map(s => ({ ...s })));
    const showTable = ref(false);
    const searchInput = ref('');
    const searchQuery = useDebounce(searchInput, props.searchDebounce);

    // The sort handed to fetchPage: the table's first sort key, and the LAST one while the table
    // has none (a third click on a header without must-sort clears the table's sortBy — the rows
    // keep the order they had). Before any sort: defaultSort's first, else {}.
    let appliedSort = props.defaultSort[0] || {};

    const computedHeaders = computed(() => {
        return props.headers.map(header => ({
            ...header,
            title: header.titleKey ? t(header.titleKey) : header.title
        }));
    });

    // Only the LATEST load may apply its result. Loads overlap (a filter tick lands mid-load, the
    // user pages on during a slow one), and a stale result arriving last would put another page's
    // rows under the footer.
    let loadSeq = 0;

    const loadItems = async (options = {}) => {
        const seq = ++loadSeq;
        loading.value = true;

        if (options.sortBy && options.sortBy.length) {
            appliedSort = {
                key: options.sortBy[0].key,
                order: options.sortBy[0].order
            };
        }
        const request = {
            page: options.page || page.value,
            itemsPerPage: options.itemsPerPage || itemsPerPage.value,
            sort: appliedSort,
            search: searchQuery.value
        };

        try {
            const result = await props.fetchPage(request);
            // A newer load has started since this one: its result wins
            if (seq !== loadSeq) return;

            items.value = result?.items || [];
            totalItems.value = Number(result?.totalItems) || 0;

            emit('itemsLoaded', { ...request, items: items.value, totalItems: totalItems.value });
        } catch (error) {
            if (seq !== loadSeq) return;
            console.error('Error loading data:', error);
            // No rows and no total: the footer reads 0-0 of 0 over an empty table, never the
            // previous page's rows under a moved footer
            items.value = [];
            totalItems.value = 0;
            emit('error', error);
        } finally {
            if (seq === loadSeq) loading.value = false;
        }
    };

    // Back to page 1 and reload, keeping the footer in step with the rows. Off page 1, moving
    // `page` is enough: the table sees its v-model change and emits update:options → loadItems.
    // On page 1 nothing changes for the table, so the load is called directly.
    const reloadFromFirstPage = () => {
        if (page.value !== 1) {
            page.value = 1;
            return;
        }
        loadItems({ page: 1 });
    };

    // A new search term starts over from page 1. This pre-flush watcher moves `page` BEFORE the
    // re-render hands the table the new term, so the table sees both at once and emits a single
    // update:options with page 1 (moved by the table itself, its search reset would emit once for
    // the old page and again for page 1).
    watch(searchQuery, () => {
        page.value = 1;
    });

    onMounted(() => {
        showTable.value = true;
    });

    // `filterContext` may be an ARRAY of contexts: a tick from ANY of them reloads —
    // BaseTimeSeriesChart's stand-in, verbatim: the composable deep-watches one object's
    // `filtersLastUpdated`, so an array goes in as one stand-in listing every context's tick ref,
    // rebuilt only when the contexts themselves change (an inline array literal does not reload on
    // a re-render).
    let multiContext = null;
    const refreshContext = computed(() => {
        const ctx = props.filterContext;
        if (!Array.isArray(ctx)) return ctx;
        const ticks = ctx.map((c) => c?.filtersLastUpdated);
        const prev = multiContext?.filtersLastUpdated;
        if (!prev || prev.length !== ticks.length || ticks.some((tick, i) => tick !== prev[i])) {
            multiContext = { filtersLastUpdated: ticks };
        }
        return multiContext;
    });

    // A filter tick, `dataLoaded` turning true or a `reloadKey` change → back to page 1 after a
    // 100 ms trailing debounce (a burst of ticks reloads once). Not `immediate`: the table's own
    // first update:options is the first load. Not gated on `dataLoaded`: a provider over a gated
    // connection answers with no rows before the data load, as it always did.
    const { refresh } = useFilterContextRefresh(
        refreshContext,
        computed(() => props.dataLoaded),
        reloadFromFirstPage,
        { debounce: 100, immediate: false, requireDataLoaded: false }
    );
    watch(() => props.reloadKey, () => refresh());

    // The parent's template ref sees only these (HEAD's BaseGrid exposed `loadItems`, whose
    // `loadItems({ page: 1 })` from outside loaded page 1 under the table's current page): `reload()`
    // goes back to page 1 the footer-safe way, at once (no debounce); `items` is the page on screen.
    expose({ reload: reloadFromFirstPage, items });

    return {
        t,
        locale: i18n.locale,
        isMobile,
        loading,
        items,
        totalItems,
        page,
        itemsPerPage,
        sortBy,
        showTable,
        searchInput,
        searchQuery,
        computedHeaders,
        mobileHeaders: MOBILE_HEADERS,
        getNestedValue,
        loadItems
    };
};
